import { modifier } from 'ember-modifier';

const VIEWPORT_PAD = 8;

/**
 * Modifier that clamps a fixed-position tooltip to stay within the viewport.
 *
 * Works by measuring the element's final rendered position (after CSS transforms)
 * and nudging `left`/`top` if any edge overflows. Because the transform offset is
 * constant, shifting `left`/`top` by a delta shifts the final position by the same
 * delta — no need to recalculate transforms.
 *
 * Pass a positional value that changes whenever the tooltip position changes so
 * the modifier reruns after `left`/`top` updates.
 *
 * Usage: `{{clampTooltip this.tooltipStyle}}` on any `position: fixed` tooltip element.
 */
const clampTooltip = modifier(function clampTooltip(
  element: HTMLElement,
  [_rerenderKey]: [unknown?]
) {
  void _rerenderKey;
  const rect = element.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let dx = 0;
  let dy = 0;

  // Clamp horizontal
  const overflowRight = rect.right - (vw - VIEWPORT_PAD);
  const overflowLeft = VIEWPORT_PAD - rect.left;

  if (overflowRight > 0) dx = -overflowRight;
  if (rect.left + dx < VIEWPORT_PAD) dx = overflowLeft;

  // Clamp vertical
  const overflowBottom = rect.bottom - (vh - VIEWPORT_PAD);
  const overflowTop = VIEWPORT_PAD - rect.top;

  if (overflowBottom > 0) dy = -overflowBottom;
  if (rect.top + dy < VIEWPORT_PAD) dy = overflowTop;

  if (dx !== 0 || dy !== 0) {
    const currentLeft = parseFloat(element.style.left) || 0;
    const currentTop = parseFloat(element.style.top) || 0;
    element.style.left = `${currentLeft + dx}px`;
    element.style.top = `${currentTop + dy}px`;
  }
});

export default clampTooltip;
